import { useEffect, useState } from 'react';
import { Icon } from '../design-system/icons';
import { Btn } from '../design-system/primitives';
import { TagInput, type TagSuggestion } from '../design-system/TagInput';
import type { FolderEntry } from '../views/collection/folders/folder-source';
import { FormatBadge } from '../views/collection/folders/FolderViews';
import { useAppServices } from '../views/ViewContext';
import { countOf, dateLabel, dimensionsLabel, documentKind, extensionOf, LABELS, SCAN_LABELS, sizeLabel, type FileEntry } from './media-format';
import type { Library } from './media-views';

/**
 * Panneau « Détails » de la médiathèque (ADR-106 ; ADR-149 §5 : panneau d'informations ⓘ) : image
 * (tailles servies, nom, étiquettes, description), document (aperçu PDF dans la page, analyse), dossier
 * (contenu, emplacement, description). Chaque enregistrement est une commande, autorisée au serveur.
 */

function PanelHead({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <header className="ay-media__panel-head">
      <strong>{title}</strong>
      <button type="button" aria-label="Fermer" onClick={onClose}><Icon name="close" size={16} /></button>
    </header>
  );
}

/** Propriétés d'un dossier : où il est, ce qu'il contient, sa description (seule propriété modifiable ici). */
export function FolderPanel({ folder, library, place, onClose, onOpen, onRename, onSave }: { folder: FolderEntry; library: Library; place: string; onClose: () => void; onOpen: () => void; onRename: () => void; onSave: (description: string) => Promise<boolean> }) {
  const [description, setDescription] = useState(folder.description ?? '');
  const [busy, setBusy] = useState(false);
  const labels = LABELS[library];
  const changed = description.trim() !== (folder.description ?? '');
  return (
    <aside className="ay-media__panel" aria-label={`Propriétés du dossier ${folder.name}`}>
      <PanelHead title={folder.name} onClose={onClose} />
      <form
        className="ay-media__panel-form"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          void onSave(description.trim()).finally(() => setBusy(false));
        }}
      >
        <div className="ay-media__panel-body">
          <div className="ay-media__folder-hero">
            <Icon name="folder" size={36} />
            <div>
              <strong>{folder.name}</strong>
              <span>{`${countOf(folder.item_count ?? 0, labels.one, labels.many)} · ${countOf(folder.folder_count ?? 0, 'sous-dossier', 'sous-dossiers')}`}</span>
            </div>
            {folder.locked && <span className="ay-media__system">Système</span>}
          </div>
          <label className="ay-field">
            <span className="ay-field__label">Description</span>
            <textarea rows={4} value={description} maxLength={2000} disabled={folder.readonly} placeholder="Ce que ce dossier rassemble, pour qui, comment le ranger…" onChange={(event) => setDescription(event.target.value)} />
          </label>
          <dl className="ay-media__facts">
            <dt>Médiathèque</dt><dd>{labels.title}</dd>
            <dt>Emplacement</dt><dd>{place}</dd>
            <dt>Type</dt><dd>{folder.locked ? 'Dossier système (nom et place fixés)' : folder.readonly ? 'Lecture seule' : 'Dossier'}</dd>
            {folder.created_at !== undefined && <><dt>Créé le</dt><dd>{dateLabel(folder.created_at)}</dd></>}
          </dl>
        </div>
        <footer className="ay-media__panel-foot">
          <Btn variant="dark" type="submit" disabled={busy || !changed || folder.readonly}>Enregistrer</Btn>
          <Btn variant="soft" icon="folder" onClick={onOpen}>Ouvrir</Btn>
          <Btn variant="soft" icon="pencil" disabled={folder.locked} {...(folder.locked ? { title: 'Dossier système : son nom est fixé' } : {})} onClick={onRename}>Renommer</Btn>
        </footer>
      </form>
    </aside>
  );
}

/** Tailles servies d'une image (spec 192 §6.2) ; les carrés se montrent à leur taille réelle. */
const SERVED_SIZES: ReadonlyArray<{ id: string; label: string; actual?: boolean }> = [
  { id: 'square_64', label: '64 × 64', actual: true },
  { id: 'square_200', label: '200 × 200', actual: true },
  { id: 'square_500', label: '500 × 500', actual: true },
  { id: 'detail', label: 'Détail 1 280' },
  { id: 'original', label: 'Original' },
];
const sizeName = (size: { id: string; label: string }, file: FileEntry): string => (size.id === 'original' && dimensionsLabel(file) !== undefined ? `Original ${dimensionsLabel(file)}` : size.label);

interface Served {
  url: string;
  preset?: string;
  media_type: string;
  size_bytes: number;
}

export function ImagePanel({ file, preview, suggestions, onClose, onSave }: { file: FileEntry; preview: string | undefined; suggestions: readonly TagSuggestion[]; onClose: () => void; onSave: (patch: Record<string, unknown>) => Promise<boolean> }) {
  const services = useAppServices();
  const [title, setTitle] = useState(file.title);
  const [tags, setTags] = useState<string[]>(file.tags);
  const [description, setDescription] = useState(file.description ?? '');
  const [busy, setBusy] = useState(false);
  const [size, setSize] = useState('detail');
  const [served, setServed] = useState<Served | null>(null);
  // Le serveur dit ce qu'il sert : la variante demandée (`preset` présent) ou, en attendant, l'original.
  useEffect(() => {
    let alive = true;
    setServed(null);
    services.client
      .query<{ preset?: string; media_type: string; size_bytes: number; download: { url: string } }>('GetFileDownload', { file_id: file.id, ...(size === 'original' ? {} : { preset: size }) })
      .then((response) => alive && setServed({ url: response.data.download.url, media_type: response.data.media_type, size_bytes: response.data.size_bytes, ...(response.data.preset === undefined ? {} : { preset: response.data.preset }) }))
      .catch(() => alive && setServed(null));
    return () => {
      alive = false;
    };
  }, [services.client, file.id, size]);
  const current = SERVED_SIZES.find((entry) => entry.id === size)!;
  const pending = served !== null && size !== 'original' && served.preset === undefined;
  const shown = served?.url ?? (size === 'detail' ? preview : undefined);
  const download = async (): Promise<void> => {
    const response = await services.client.query<{ download: { url: string } }>('GetFileDownload', { file_id: file.id });
    globalThis.open?.(response.data.download.url, '_blank', 'noopener');
  };
  return (
    <aside className="ay-media__panel" aria-label={`Propriétés de ${file.title}`}>
      <PanelHead title={file.title} onClose={onClose} />
      <form
        className="ay-media__panel-form"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          void onSave({ title: title.trim(), tags, description: description.trim() }).finally(() => setBusy(false));
        }}
      >
        <div className="ay-media__panel-body">
          <button type="button" className={`ay-media__preview${current.actual === true && !pending ? ' ay-media__preview--actual' : ''}`} onClick={() => served !== null && globalThis.open?.(served.url, '_blank', 'noopener')} aria-label={`Ouvrir en grand — ${current.label}`}>
            {shown !== undefined ? <img src={shown} alt={`${file.title}, ${current.label}`} /> : <FormatBadge text={extensionOf(file)} tone="warning" size="lg" />}
          </button>
          <div className="ay-media__presets" role="radiogroup" aria-label="Tailles servies">
            {SERVED_SIZES.map((entry) => (
              <button key={entry.id} type="button" role="radio" aria-checked={entry.id === size} className="ay-media__preset" onClick={() => setSize(entry.id)}>
                {sizeName(entry, file)}
              </button>
            ))}
          </div>
          <span className={`ay-media__caption${pending ? ' ay-media__caption--pending' : ''}`} role="status">
            {served === null
              ? 'Chargement…'
              : pending
                ? `${current.label} pas encore calculée : l’original est montré en attendant.`
                : `${sizeName(current, file)} · ${sizeLabel(served.size_bytes)} · ${served.media_type.replace('image/', '').toUpperCase()}${current.actual === true ? ' · taille réelle' : ''}`}
          </span>
          <label className="ay-field"><span className="ay-field__label">Nom affiché</span><input value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} /></label>
          <TagInput label="Étiquettes" value={tags} onChange={setTags} suggestions={suggestions} />
          <label className="ay-field"><span className="ay-field__label">Description</span><textarea rows={3} value={description} maxLength={2000} onChange={(event) => setDescription(event.target.value)} /></label>
          <dl className="ay-media__facts">
            <dt>Fichier</dt><dd className="ay-mono">{file.file_name}</dd>
            <dt>Taille</dt><dd>{sizeLabel(file.size_bytes)}</dd>
            <dt>Déposée</dt><dd>{dateLabel(file.created_at)}</dd>
            <dt>Données GPS</dt><dd>Retirées des tailles servies</dd>
          </dl>
        </div>
        <footer className="ay-media__panel-foot">
          <Btn variant="dark" type="submit" disabled={busy || title.trim() === ''}>Enregistrer</Btn>
          <Btn variant="soft" disabled title="L’éditeur d’image (recadrage, rotation, détourage) arrive avec la tranche suivante de la spec 192.">Éditer l’image</Btn>
          <Btn variant="soft" onClick={() => void download()}>Télécharger</Btn>
        </footer>
      </form>
    </aside>
  );
}

export function DocumentPanel({ file, imagePreview, place, onClose, onMove, onSave, suggestions }: { file: FileEntry; imagePreview: string | undefined; place: string; onClose: () => void; onMove: () => void; onSave: (patch: Record<string, unknown>) => Promise<boolean>; suggestions: readonly TagSuggestion[] }) {
  const services = useAppServices();
  const [inline, setInline] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>(file.tags);
  const kind = documentKind(file);
  const isPdf = file.media_type === 'application/pdf';
  useEffect(() => {
    if (!isPdf) return;
    let alive = true;
    services.client
      .query<{ download: { url: string } }>('GetFileDownload', { file_id: file.id })
      .then((response) => alive && setInline(response.data.download.url))
      .catch(() => alive && setInline(null));
    return () => {
      alive = false;
    };
  }, [services.client, file.id, isPdf]);
  const download = async (): Promise<void> => {
    const response = await services.client.query<{ download: { url: string } }>('GetFileDownload', { file_id: file.id });
    globalThis.open?.(response.data.download.url, '_blank', 'noopener');
  };
  const tagsChanged = tags.join('\u0000') !== file.tags.join('\u0000');
  return (
    <aside className="ay-media__panel ay-media__panel--document" aria-label={`Aperçu de ${file.title}`}>
      <PanelHead title={file.title} onClose={onClose} />
      <div className="ay-media__panel-body">
        <div className="ay-media__page" aria-label="Aperçu de la première page">
          {isPdf && inline !== null ? (
            <iframe title={`Aperçu de ${file.title}`} src={`${inline}#page=1&toolbar=0&view=FitH`} />
          ) : imagePreview !== undefined ? (
            <img src={imagePreview} alt="" />
          ) : (
            <FormatBadge text={extensionOf(file)} tone={kind.tone} size="lg" />
          )}
        </div>
        <span className="ay-media__caption">
          {file.page_count !== undefined ? `Page 1 sur ${file.page_count} · ` : ''}
          {isPdf || file.media_type.startsWith('image/') ? 's’ouvre dans la page, sans téléchargement' : 'aperçu après conversion en PDF (tranche suivante)'}
        </span>
        <dl className="ay-media__facts">
          <dt>Format</dt><dd>{kind.format}</dd>
          <dt>Taille</dt><dd>{sizeLabel(file.size_bytes)}</dd>
          <dt>Emplacement</dt><dd>{place}</dd>
          <dt>Déposé le</dt><dd>{dateLabel(file.created_at)}</dd>
          <dt>Lié à</dt><dd className="ay-mono">—</dd>
          <dt>Analyse</dt><dd>{SCAN_LABELS[file.scan_status ?? 'pending'] ?? 'Analyse en attente'}</dd>
        </dl>
        <TagInput label="Étiquettes" value={tags} onChange={setTags} suggestions={suggestions} />
      </div>
      <footer className="ay-media__panel-foot">
        <Btn variant="dark" onClick={() => void download()}>Ouvrir en grand</Btn>
        {tagsChanged && <Btn variant="soft" onClick={() => void onSave({ tags })}>Enregistrer les étiquettes</Btn>}
        <Btn variant="soft" onClick={() => void download()}>Télécharger</Btn>
        <Btn variant="soft" icon="move" onClick={onMove}>Déplacer vers…</Btn>
      </footer>
    </aside>
  );
}
